import {
  DEFAULT_SETTINGS_SECTION,
  isSettingsSection,
  SETTINGS_SECTION,
  type SettingsSection,
} from "@/lib/settings/sections";

export type HomePathOptions = {
  prompt?: string | null;
  model?: string | null;
  effort?: string | null;
  agent?: string | null;
};
export const homePath = (options: HomePathOptions = {}) => {
  const params = new URLSearchParams();
  for (const key of ["prompt", "model", "effort", "agent"] as const) {
    const value = options[key];
    if (value) params.set(key, value);
  }
  const query = params.toString();
  return query ? `/?${query}` : "/";
};
/** `prompt` prefills the composer without sending it. */
export const conversationPath = (
  id: string,
  options: { prompt?: string } = {},
) =>
  `/chats/${encodeURIComponent(id)}${
    options.prompt ? `?${new URLSearchParams({ prompt: options.prompt })}` : ""
  }`;
export const agentsPath = () => "/agents";
export const agentPath = (agentId: string) =>
  `/agents/${encodeURIComponent(agentId)}`;
export const filesPath = () => "/files";
export const chatsPath = () => "/chats";
export const dashboardsPath = () => "/dashboards";
export const dashboardPath = (dashboardId: string) =>
  `/dashboards/${encodeURIComponent(dashboardId)}`;
/** Opens the dashboard a chat links to by its workspace path. */
export const dashboardOpenPath = (conversationId: string, path: string) =>
  `/dashboards/open?${new URLSearchParams({ conversationId, path })}`;
export const docsPath = (slug?: string) =>
  slug ? `/docs/${encodeURIComponent(slug)}` : "/docs";
export const meetingsUserPath = () => "/meetings";
export const meetingsNewUserPath = () => "/meetings/new";
export const meetingUserDetailPath = (meetingId: string) =>
  `/meetings/${encodeURIComponent(meetingId)}`;

const SETTINGS_HASH_PREFIX = "#settings";
const PROMPTS_HASH_PREFIX = "#prompts";
const FEEDBACK_HASH = "#feedback";
const DASHBOARD_CREATE_HASH = "#dashboards/new";

const SETTINGS_SUBPATH_MATCHERS: Partial<
  Record<SettingsSection, (subPath: readonly string[]) => boolean>
> = {
  [SETTINGS_SECTION.MEETINGS]: (segments) =>
    segments.length === 2 &&
    (segments[0] === "templates" ||
      segments[0] === "dictionary" ||
      segments[0] === "tags"),
  [SETTINGS_SECTION.USERS]: (segments) => segments.length === 1,
  [SETTINGS_SECTION.GROUPS]: (segments) =>
    segments.length === 1 ||
    (segments.length === 2 &&
      segments[1] === "members" &&
      segments[0] !== "new"),
  [SETTINGS_SECTION.TENANTS]: (segments) => segments.length === 1,
  [SETTINGS_SECTION.CREDITS]: (segments) =>
    (segments.length === 1 && segments[0] !== "history") ||
    (segments.length === 2 && segments[1] === "edit"),
  [SETTINGS_SECTION.BOTS]: (segments) => segments.length === 1,
  [SETTINGS_SECTION.ANNOUNCEMENTS]: (segments) => segments.length === 1,
};

export type SettingsHashResolution =
  | {
      isSettingsHash: true;
      section: SettingsSection;
      subPath: string[];
      /** 表の検索・絞り込み・並び替え（`?`を除いたクエリ）。 */
      query: string;
      hash: string;
      shouldReplace: boolean;
    }
  | {
      isSettingsHash: false;
    };

/** ハッシュを、`/`で区切ったパスと`?`以降のクエリに分ける。 */
function splitHash(rawHash: string) {
  const withoutPrefix = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
  const firstChunk = withoutPrefix.split("#", 1)[0] ?? withoutPrefix;
  const queryStart = firstChunk.indexOf("?");
  const path = queryStart === -1 ? firstChunk : firstChunk.slice(0, queryStart);
  const query =
    queryStart === -1
      ? ""
      : new URLSearchParams(firstChunk.slice(queryStart + 1)).toString();
  const segments =
    path === ""
      ? []
      : path.split("/").map((segment) => {
          try {
            return decodeURIComponent(segment);
          } catch {
            return segment;
          }
        });
  return { segments, query };
}

function splitHashSegments(rawHash: string) {
  return splitHash(rawHash).segments;
}

export function isAgentEditorHref(href: string) {
  const pathname = href.split(/[?#]/, 1)[0] ?? href;
  return /^\/agents\/[^/]+$/.test(pathname);
}

function encodeSubPath(subPath: readonly string[]) {
  return subPath.map((segment) => encodeURIComponent(segment)).join("/");
}

export function buildSettingsSubPathHash(
  section: SettingsSection,
  subPath: readonly string[] = [],
  query = "",
) {
  const sectionHash = `${SETTINGS_HASH_PREFIX}/${section}`;
  const pathHash =
    subPath.length > 0
      ? `${sectionHash}/${encodeSubPath(subPath)}`
      : sectionHash;
  return query ? `${pathHash}?${query}` : pathHash;
}

export function buildSettingsHashHref(args: {
  section?: SettingsSection;
  subPath?: readonly string[];
}) {
  return `${homePath()}${buildSettingsSubPathHash(
    args.section ?? DEFAULT_SETTINGS_SECTION,
    args.subPath,
  )}`;
}

export function resolveSettingsHash(rawHash: string): SettingsHashResolution {
  const { segments, query: requestedQuery } = splitHash(rawHash);
  if (segments[0] !== "settings") {
    return { isSettingsHash: false };
  }

  const requestedSection = segments[1];
  const section = isSettingsSection(requestedSection)
    ? requestedSection
    : DEFAULT_SETTINGS_SECTION;
  const subPathCandidate = isSettingsSection(requestedSection)
    ? segments.slice(2)
    : [];
  const matcher = SETTINGS_SUBPATH_MATCHERS[section];
  const subPath =
    matcher &&
    subPathCandidate.length > 0 &&
    subPathCandidate.every((segment) => segment !== "") &&
    matcher(subPathCandidate)
      ? subPathCandidate
      : [];
  // 区分が不正で既定の区分を開くときは、別の表の条件を引き継がない。
  const query = isSettingsSection(requestedSection) ? requestedQuery : "";
  const hash = buildSettingsSubPathHash(section, subPath, query);

  return {
    isSettingsHash: true,
    section,
    subPath,
    query,
    hash,
    shouldReplace: rawHash !== hash,
  };
}

export type PromptHashTarget = { kind: "new" } | { id: string; kind: "edit" };

export function promptHash(target: "new" | string) {
  return `${PROMPTS_HASH_PREFIX}/${encodeURIComponent(target)}`;
}

export function isPromptHash(rawHash: string) {
  return splitHashSegments(rawHash)[0] === "prompts";
}

export function resolvePromptHash(rawHash: string): PromptHashTarget | null {
  const segments = splitHashSegments(rawHash);
  if (segments[0] !== "prompts" || segments.length !== 2 || !segments[1]) {
    return null;
  }
  return segments[1] === "new"
    ? { kind: "new" }
    : { id: segments[1], kind: "edit" };
}

export function dashboardCreateHash() {
  return DASHBOARD_CREATE_HASH;
}

export function isDashboardCreateHash(rawHash: string) {
  return splitHashSegments(rawHash).join("/") === "dashboards/new";
}

export function feedbackHash() {
  return FEEDBACK_HASH;
}

export function isFeedbackHash(rawHash: string) {
  return splitHashSegments(rawHash).join("/") === "feedback";
}

/** ハッシュモーダルを持たない画面（サポートサイトなど）からの遷移先。 */
export function feedbackHashHref() {
  return `${homePath()}${FEEDBACK_HASH}`;
}
